import { useEffect, useState } from "react";
import { categoryApi } from "@/services/api";
import type { Category } from "@/models/types";

interface CategoryBadgeProps {
  categoryId: string;
  showName?: boolean;
  size?: "sm" | "md";
}

// Caché a nivel de módulo para evitar fetch por cada badge
let cachedCategories: Category[] | null = null;
let pendingFetch: Promise<Category[]> | null = null;

async function loadCategories(): Promise<Category[]> {
  if (cachedCategories) return cachedCategories;
  if (!pendingFetch) {
    pendingFetch = categoryApi.list().then((data) => {
      cachedCategories = data;
      pendingFetch = null;
      return data;
    }).catch((err) => {
      pendingFetch = null;
      throw err;
    });
  }
  return pendingFetch;
}

// Permite invalidar la caché si en algún momento se crean/editan categorías
export function invalidateCategoryCache() {
  cachedCategories = null;
}

export default function CategoryBadge({
  categoryId,
  showName = true,
  size = "sm",
}: CategoryBadgeProps) {
  const [category, setCategory] = useState<Category | null>(
    cachedCategories?.find((c) => c.id === categoryId) ?? null,
  );
  const [loaded, setLoaded] = useState(!!cachedCategories);

  useEffect(() => {
    let mounted = true;
    if (cachedCategories) {
      setCategory(cachedCategories.find((c) => c.id === categoryId) ?? null);
      setLoaded(true);
      return;
    }
    loadCategories()
      .then((data) => {
        if (!mounted) return;
        setCategory(data.find((c) => c.id === categoryId) ?? null);
        setLoaded(true);
      })
      .catch(() => {
        if (!mounted) return;
        setLoaded(true);
      });
    return () => {
      mounted = false;
    };
  }, [categoryId]);

  if (!loaded) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-foreground-400">
        <span className="w-5 h-5 rounded-full bg-background-200 animate-pulse" />
      </span>
    );
  }

  if (!category) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
        <span className="w-5 h-5 rounded-full bg-background-200 flex items-center justify-center">
          <i className="ri-question-line text-[10px]" />
        </span>
        {showName && "Sin categoría"}
      </span>
    );
  }

  const iconSize = size === "md" ? "w-8 h-8 text-base" : "w-5 h-5 text-xs";
  const textSize = size === "md" ? "text-sm" : "text-xs";

  return (
    <span className={`inline-flex items-center gap-1.5 ${textSize} text-foreground-700`}>
      <span
        className={`${iconSize} rounded-full flex items-center justify-center`}
        style={{
          backgroundColor: `${category.color}20`,
          color: category.color,
        }}
      >
        <i className={category.icon} />
      </span>
      {showName && <span className="truncate">{category.name}</span>}
    </span>
  );
}